import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import {
  RiskScoreItem,
  IncorporationDateFormValues,
  SicCode,
  ClientRange,
  ProductsValidators,
  ProductFormValues,
  DeliveryChannelFormValues,
  DeliveryChannelValidators,
  ClientRangeFormValues,
} from '../../types';

export interface ProductsValidatorsResponse {
  data: ProductsValidators;
}

export interface DeliveryChannelValidatorsResponse {
  data: DeliveryChannelValidators;
}

export const corporateClientsRiskApi = api.injectEndpoints({
  endpoints: (build) => ({
    getIncorporationDates: build.query<Response<RiskScoreItem>, string>({
      query: (query) => ({
        url: `/v1/incorporation-dates${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIncorporationDate: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/incorporation-dates/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIncorporationDate: build.mutation<
      {
        data: RiskScoreItem;
      },
      IncorporationDateFormValues
    >({
      query: (body: IncorporationDateFormValues) => ({
        url: '/v1/incorporation-dates',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIncorporationDate: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: IncorporationDateFormValues; id: string }
    >({
      query: (data: { body: IncorporationDateFormValues; id: string }) => ({
        url: `/v1/incorporation-dates/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIncorporationDate: build.mutation<
      {
        data: RiskScoreItem;
      },
      string
    >({
      query: (id: string) => ({
        url: `/v1/incorporation-dates/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSicCodes: build.query<Response<SicCode>, string>({
      query: (query) => ({
        url: `/v1/sic-codes${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSicCode: build.query<{ data: SicCode }, string>({
      query: (id) => ({
        url: `/v1/sic-codes/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateSicCodeRiskScore: build.mutation<
      {
        data: SicCode;
      },
      { body: { riskScore: number }; id: string }
    >({
      query: (data) => ({
        url: `/v1/sic-codes/${data.id}/risk-score`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateVolumes: build.query<Response<ClientRange>, string>({
      query: (query) => ({
        url: `/v1/settings-clients-ranges${query}&all=true&clientType=CORPORATE`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateVolume: build.query<{ data: ClientRange }, string>({
      query: (id) => ({
        url: `/v1/settings-clients-ranges/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateVolumes: build.mutation<
      {
        data: ClientRange;
      },
      ClientRangeFormValues
    >({
      query: (body: ClientRangeFormValues) => ({
        url: '/v1/settings-clients-ranges',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateVolumes: build.mutation<
      {
        data: ClientRange;
      },
      { id: string; body: ClientRangeFormValues }
    >({
      query: (data) => ({
        url: `/v1/settings-clients-ranges/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteCorporateVolumes: build.mutation<
      {
        count: number;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/settings-clients-ranges/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProductsValidatorsCorporate: build.query<
      ProductsValidatorsResponse,
      void
    >({
      query: () => ({
        url: '/v1/products/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProductsCorporate: build.query<Response<RiskScoreItem>, string>({
      query: (query) => ({
        url: `/v1/products${query}&clientType=CORPORATE`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateProduct: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/products/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateProduct: build.mutation<
      {
        data: RiskScoreItem;
      },
      ProductFormValues
    >({
      query: (body: ProductFormValues) => ({
        url: '/v1/products',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateProduct: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: ProductFormValues; id: string }
    >({
      query: (data: { body: ProductFormValues; id: string }) => ({
        url: `/v1/products/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateDeliveryChannelValidators: build.query<
      DeliveryChannelValidatorsResponse,
      void
    >({
      query: () => ({
        url: '/v1/delivery-channels/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateDeliveryChannels: build.query<Response<RiskScoreItem>, string>({
      query: (query) => ({
        url: `/v1/delivery-channels${query}&clientType=CORPORATE`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCorporateDeliveryChannel: build.query<{ data: RiskScoreItem }, string>({
      query: (id) => ({
        url: `/v1/delivery-channels/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCorporateDeliveryChannel: build.mutation<
      {
        data: RiskScoreItem;
      },
      DeliveryChannelFormValues
    >({
      query: (body: DeliveryChannelFormValues) => ({
        url: '/v1/delivery-channels',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCorporateDeliveryChannel: build.mutation<
      {
        data: RiskScoreItem;
      },
      { body: DeliveryChannelFormValues; id: string }
    >({
      query: (data: { body: DeliveryChannelFormValues; id: string }) => ({
        url: `/v1/delivery-channels/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetIncorporationDateQuery,
  useLazyGetIncorporationDatesQuery,
  useCreateIncorporationDateMutation,
  useUpdateIncorporationDateMutation,
  useDeleteIncorporationDateMutation,
  useLazyGetSicCodesQuery,
  useLazyGetCorporateVolumesQuery,
  useGetProductsValidatorsCorporateQuery,
  useLazyGetCorporateProductQuery,
  useLazyGetProductsCorporateQuery,
  useCreateCorporateProductMutation,
  useUpdateCorporateProductMutation,
  useLazyGetCorporateDeliveryChannelQuery,
  useLazyGetCorporateDeliveryChannelValidatorsQuery,
  useLazyGetCorporateDeliveryChannelsQuery,
  useCreateCorporateDeliveryChannelMutation,
  useUpdateCorporateDeliveryChannelMutation,
  useCreateCorporateVolumesMutation,
  useLazyGetCorporateVolumeQuery,
  useUpdateCorporateVolumesMutation,
  useBatchDeleteCorporateVolumesMutation,
  useLazyGetSicCodeQuery,
  useUpdateSicCodeRiskScoreMutation,
} = corporateClientsRiskApi;

export const {
  endpoints: {
    getIncorporationDate,
    getIncorporationDates,
    updateIncorporationDate,
    deleteIncorporationDate,
    createIncorporationDate,
    getSicCodes,
    getCorporateVolumes,
    getProductsCorporate,
    getProductsValidatorsCorporate,
    getCorporateProduct,
    createCorporateProduct,
    updateCorporateProduct,
    getCorporateDeliveryChannel,
    getCorporateDeliveryChannelValidators,
    getCorporateDeliveryChannels,
    createCorporateDeliveryChannel,
    updateCorporateDeliveryChannel,
    getCorporateVolume,
    updateCorporateVolumes,
    batchDeleteCorporateVolumes,
    getSicCode,
    updateSicCodeRiskScore,
  },
} = corporateClientsRiskApi;
